import React, { useState, useEffect, useRef } from 'react';
import { downloadPdf } from '../utils/htmlToPdf';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Printer, X, Loader2, Download } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Expense category display names
const EXPENSE_CATEGORY_NAMES = {
  'fnb': 'F&B',
  'hrdc_levy': 'HRDCorp Levy',
  'wear_tear': 'Wear & Tear',
  'printing': 'Printing',
  'transportation': 'Transportation',
  'accommodation': 'Accommodation',
  'venue': 'Venue Rental',
  'equipment': 'Equipment',
  'materials': 'Materials',
  'miscellaneous': 'Miscellaneous',
  'other': 'Other'
};

const getExpenseCategoryName = (category) => {
  if (!category) return 'Other';
  return EXPENSE_CATEGORY_NAMES[category.toLowerCase()] || category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const ClaimFormPrint = ({ session, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [costingData, setCostingData] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const printRef = useRef(null);

  // Phase 3A FINAL Section 7 + MINI PHASE 3 (J): the four Claim Form
  // headline totals — session_revenue, session_cost, gross_profit,
  // gross_margin_pct — come ONLY from the canonical session snapshot.
  // If the snapshot fails or lacks ANY of those fields (zero is valid,
  // missing is not) we set financialTotalsUnavailable=true, show a clear
  // message, and hide the Download button so no misleading document is
  // produced.
  const [sotError, setSotError] = useState(null);
  const [financialTotalsUnavailable, setFinancialTotalsUnavailable] = useState(false);
  const loadTokenRef = React.useRef(0);
  useEffect(() => {
    loadTokenRef.current += 1;
    loadData(loadTokenRef.current);
  }, [session.id]);

  const REQUIRED_SNAPSHOT_FIELDS = [
    'session_revenue',
    'session_cost',
    'gross_profit',
    'gross_margin_pct',
  ];

  const loadData = async (token) => {
    try {
      setSotError(null);
      setFinancialTotalsUnavailable(false);
      const [costingRes, settingsRes, sotRes] = await Promise.all([
        axiosInstance.get(`/finance/session/${session.id}/costing`),
        axiosInstance.get('/finance/company-settings'),
        axiosInstance.get(`/finance/source-of-truth/session/${session.id}`)
      ]);
      if (token !== loadTokenRef.current) return;

      setCostingData(costingRes.data);
      setCompanySettings(settingsRes.data);

      const sot = sotRes.data || {};
      const realInvoices = Array.isArray(sot.invoices) ? sot.invoices : [];

      // MINI PHASE 3 (J1/J2): fetch the canonical SESSION snapshot. It is
      // the ONE authoritative source for headline totals. If it fails or
      // any required field is not PRESENT on the returned object (zero is
      // valid, missing is not), mark headline totals unavailable.
      let sessionSnapshot = null;
      let snapshotOk = false;
      try {
        const sRes = await axiosInstance.get(
          `/finance/source-of-truth/session/${session.id}/snapshot`,
        );
        sessionSnapshot = sRes.data || null;
        if (sessionSnapshot && typeof sessionSnapshot === 'object') {
          const missing = REQUIRED_SNAPSHOT_FIELDS.filter(
            (f) => !Object.prototype.hasOwnProperty.call(sessionSnapshot, f),
          );
          snapshotOk = missing.length === 0;
        }
      } catch (_err) {
        snapshotOk = false;
      }

      if (!snapshotOk) {
        setFinancialTotalsUnavailable(true);
      }

      // MINI PHASE 3 FINAL (J1): the four canonical headline fields are
      // ALWAYS taken from the valid session snapshot — never from
      // costingRes.data — regardless of how many revenue-eligible
      // invoices exist. Invoice-metadata enrichment stays conditional on
      // realInvoices.length > 0, but headline totals do not.
      const canonicalHeadline = snapshotOk
        ? {
            // Prefer the FORECAST fields (include drafts) so Claim Form
            // matches Session Costing even when the invoice is still
            // draft/auto_draft/approved. Falls back to strict AR values
            // for very old snapshots that lack the forecast field.
            session_revenue: Number(
              sessionSnapshot.session_revenue_forecast != null
                ? sessionSnapshot.session_revenue_forecast
                : sessionSnapshot.session_revenue
            ),
            session_cost: Number(sessionSnapshot.session_cost),
            gross_profit: Number(
              sessionSnapshot.gross_profit_forecast != null
                ? sessionSnapshot.gross_profit_forecast
                : sessionSnapshot.gross_profit
            ),
            gross_margin_pct: Number(
              sessionSnapshot.gross_margin_forecast_pct != null
                ? sessionSnapshot.gross_margin_forecast_pct
                : sessionSnapshot.gross_margin_pct
            ),
          }
        : {
            session_revenue: NaN,
            session_cost: NaN,
            gross_profit: NaN,
            gross_margin_pct: NaN,
          };

      if (realInvoices.length > 0) {
        const invoiceNumbers = realInvoices.map(inv => inv.invoice_number).filter(Boolean).join(', ');
        const primary = realInvoices[0];
        setCostingData(prev => ({
          ...prev,
          invoice_number: invoiceNumbers,
          invoice_date: primary.invoice_date || primary.invoice_created_at,
          invoice_total: Number(sot.net_invoiced_value || 0),
          less_tax: 0,
          gross_invoice_value: Number(sot.gross_invoice_value || 0),
          net_invoiced_value: Number(sot.net_invoiced_value || 0),
          credit_note_total: Number(sot.credit_note_total || 0),
          paid_amount: Number(sot.paid_amount || 0),
          outstanding_amount: Number(sot.outstanding_amount || 0),
          // MINI PHASE 3 FINAL (J1): headline totals ALWAYS canonical.
          ...canonicalHeadline,
          all_invoices: realInvoices.map(snap => ({
            invoice_number: snap.invoice_number,
            invoice_date: snap.invoice_date || snap.invoice_created_at,
            company_name: snap.company_name,
            document_face_value: Number(snap.document_face_value || 0),
            credit_note_total: Number(snap.credit_note_total || 0),
            net_invoiced_value: Number(snap.net_invoiced_value || 0),
            outstanding_amount: Number(snap.outstanding_amount || 0),
          })),
        }));
      } else {
        // No revenue-eligible invoices — still overwrite the four
        // canonical headline totals so nothing from the ordinary costing
        // endpoint leaks into the Claim Form headline rows.
        setCostingData(prev => ({
          ...prev,
          ...canonicalHeadline,
        }));
      }
    } catch (error) {
      setSotError('Financial snapshot unavailable — please retry. Values not shown to avoid displaying stale zeros.');
      setFinancialTotalsUnavailable(true);
      toast.error('Failed to load claim form data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
        const logoUrl = companySettings?.logo_url || '';
    
    const _pdfHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Course Registration Form - ${session.name}</title>
          <style>
            @page { size: A4; margin: 12mm; }
            @media print { body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } }
            
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; font-size: 10px; line-height: 1.4; padding: 15px; }
            
            /* Header */
            .header { 
              display: flex; 
              align-items: center; 
              justify-content: center;
              margin-bottom: 15px; 
              border-bottom: 2px solid #1e40af; 
              padding-bottom: 12px;
              gap: 20px;
            }
            .logo { max-width: 100px; max-height: 60px; object-fit: contain; }
            .header-text { text-align: center; }
            .company-name { font-size: 14px; font-weight: bold; color: #1e40af; margin-bottom: 4px; }
            .form-title { font-size: 12px; font-weight: bold; background: #e5e7eb; padding: 4px 12px; display: inline-block; margin-top: 4px; }
            
            /* Sections */
            .section { margin-bottom: 12px; }
            .section-header { 
              background: #1e40af; 
              color: white; 
              padding: 5px 10px; 
              font-weight: bold; 
              font-size: 10px; 
            }
            
            /* Info Grid */
            .info-grid { 
              display: grid; 
              grid-template-columns: repeat(2, 1fr); 
              gap: 12px; 
              padding: 12px; 
              border: 1px solid #000; 
              border-top: none; 
            }
            .info-grid-3 { grid-template-columns: repeat(3, 1fr); }
            .info-item { }
            .info-label { 
              font-weight: bold; 
              font-size: 9px; 
              color: #333; 
              display: block; 
              margin-bottom: 4px; 
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .info-value { 
              border-bottom: 1px solid #ccc; 
              padding: 6px 8px; 
              min-height: 26px; 
              font-size: 11px;
              background: #fafafa;
              margin-top: 2px;
            }
            .info-full { grid-column: span 2; }
            
            /* Tables */
            table { width: 100%; border-collapse: collapse; font-size: 9px; margin-top: 0; }
            th, td { border: 1px solid #000; padding: 5px 6px; text-align: left; }
            th { background: #f3f4f6; font-weight: bold; font-size: 8px; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .highlight { background: #fef9c3 !important; }
            .subtotal-row { background: #f3f4f6; font-weight: bold; }
            .total-row { background: #d1d5db; font-weight: bold; }
            .profit-row { background: #bbf7d0 !important; font-weight: bold; }
            
            /* Two Column Layout */
            .two-col { display: flex; gap: 12px; margin-bottom: 12px; }
            .col-left { flex: 1.2; }
            .col-right { flex: 0.8; }
            
            /* Costing Summary */
            .costing-table { width: 100%; }
            .costing-table td { padding: 5px 10px; border: 1px solid #000; }
            .costing-label { font-weight: bold; width: 55%; }
            .costing-value { text-align: right; width: 30%; }
            .costing-pct { text-align: right; width: 15%; }
            
            /* Acknowledgment */
            .ack-table { width: 100%; margin-top: 15px; }
            .ack-table td { padding: 10px; border: 1px solid #000; height: 50px; vertical-align: top; }
            .ack-label { font-weight: bold; width: 25%; }
            
            /* Footer */
            .footer { 
              margin-top: 15px; 
              text-align: center; 
              font-size: 8px; 
              color: #666; 
              border-top: 1px solid #ccc; 
              padding-top: 8px; 
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `;
    downloadPdf(_pdfHtml, 'ClaimForm');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const calculateDays = () => {
    if (!session.start_date || !session.end_date) return 1;
    const start = new Date(session.start_date);
    const end = new Date(session.end_date);
    return Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p>Loading claim form data...</p>
        </div>
      </div>
    );
  }

  if (!costingData) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 text-center">
          <p className="text-red-600">Failed to load data</p>
          <Button onClick={onClose} className="mt-4">Close</Button>
        </div>
      </div>
    );
  }

  const days = calculateDays();
  // ---- MINI PHASE 3 (J1/J4): headline totals are canonical ONLY.
  // No local recomputation, no fallback to net_invoiced_value, no
  // "revenue - expenses" recovery formula, no zero substitution when
  // the snapshot is unavailable. The `financialTotalsUnavailable` gate
  // below controls whether these values ever reach the DOM.
  const invoiceTotal = Number(costingData.net_invoiced_value || 0);
  const taxAmount = 0;
  const grossRevenue = Number(costingData.session_revenue);
  const trainerFeesTotal = costingData.trainer_fees_total || 0;
  const coordFeeTotal = costingData.coordinator_fee_total || 0;
  const cashExpenses = costingData.cash_expenses_actual || costingData.cash_expenses_estimated || 0;

  // Marketing amount is a derived detail row (session_cost minus the other
  // detail rows). Prefer the authoritative `marketing_commission` field
  // from costing if available so the Claim Form matches the Session Costing
  // Profit Summary exactly (bug surfaced in payables export: showed 0.00
  // because the derived subtraction yielded a negative, clamped-away value
  // when stored commission was stale).
  const marketingAmount = Math.max(
    0,
    costingData.marketing_commission != null
      ? Number(costingData.marketing_commission || 0)
      : Number(costingData.session_cost || 0)
          - trainerFeesTotal - coordFeeTotal - cashExpenses,
  );

  // Total expenses MUST be the sum of all displayed detail rows so the
  // Claim Form reconciles. Do NOT trust snapshot.session_cost here because
  // historical rows carry stale/negative marketing commissions that drag
  // session_cost below the sum of its parts.
  const totalExpenses = cashExpenses + trainerFeesTotal + coordFeeTotal + marketingAmount;
  const grossProfit = grossRevenue - totalExpenses;
  const profit = grossProfit;
  const profitPct = grossRevenue > 0 ? (grossProfit / grossRevenue * 100) : 0;
  const marketingName = costingData.marketing?.marketing_user_name || costingData.marketing?.full_name || 'N/A';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[95vh] overflow-y-auto">
        {/* Action Bar */}
        <div className="sticky top-0 bg-white border-b p-3 flex justify-between items-center z-10">
          <h2 className="text-lg font-bold">Course Registration Form (Claim Form)</h2>
          <div className="flex gap-2">
            {!financialTotalsUnavailable && (
              <Button
                onClick={handlePrint}
                className="bg-green-600 hover:bg-green-700"
                data-testid="claim-form-download-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            )}
            <Button variant="outline" onClick={onClose}>
              <X className="w-4 h-4 mr-2" />
              Close
            </Button>
          </div>
        </div>

        {financialTotalsUnavailable && (
          <div
            className="m-3 p-3 rounded border border-red-300 bg-red-50 text-red-700 text-sm"
            data-testid="claim-form-financial-unavailable"
          >
            <strong>Financial totals unavailable. Please retry.</strong>
            <div className="mt-1 text-xs">
              The authoritative session financial snapshot could not be loaded,
              so headline revenue / cost / profit / margin cannot be shown.
              Download is disabled to avoid producing a financially misleading
              Claim Form.
            </div>
          </div>
        )}

        {/* Printable Content */}
        <div ref={printRef} className="p-6 bg-white" style={{ fontSize: '11px' }}>
          {/* Header - Logo + Company Name centered */}
          <div className="header">
            {companySettings?.logo_url && (
              <img src={companySettings.logo_url.startsWith('/') ? `${API_URL}${companySettings.logo_url}` : companySettings.logo_url} alt="Logo" className="logo" />
            )}
            <div className="header-text">
              <div className="company-name">
                {companySettings?.company_name || 'MALAYSIAN DEFENSIVE DRIVING AND RIDING CENTRE SDN BHD'}
              </div>
              <div className="form-title">COURSE REGISTRATION FORM</div>
            </div>
          </div>

          {/* Course Particulars */}
          <div className="section">
            <div className="section-header" style={{ background: '#1e40af', color: 'white', padding: '5px 10px', fontWeight: 'bold', fontSize: '10px' }}>COURSE PARTICULARS</div>
            <div className="info-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', padding: '15px', border: '1px solid #000', borderTop: 'none' }}>
              <div className="info-item" style={{ gridColumn: 'span 2' }}>
                <span className="info-label" style={{ fontWeight: 'bold', fontSize: '9px', color: '#333', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>CLIENT</span>
                <div className="info-value" style={{ borderBottom: '1px solid #ccc', padding: '8px 10px', minHeight: '28px', fontSize: '11px', background: '#f9fafb' }}>{costingData.company_name}</div>
              </div>
              <div className="info-item">
                <span className="info-label" style={{ fontWeight: 'bold', fontSize: '9px', color: '#333', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>TRAINING START</span>
                <div className="info-value" style={{ borderBottom: '1px solid #ccc', padding: '8px 10px', minHeight: '28px', fontSize: '11px', background: '#f9fafb' }}>{formatDate(session.start_date)}</div>
              </div>
              <div className="info-item">
                <span className="info-label" style={{ fontWeight: 'bold', fontSize: '9px', color: '#333', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>TRAINING END</span>
                <div className="info-value" style={{ borderBottom: '1px solid #ccc', padding: '8px 10px', minHeight: '28px', fontSize: '11px', background: '#f9fafb' }}>{formatDate(session.end_date)}</div>
              </div>
              <div className="info-item">
                <span className="info-label" style={{ fontWeight: 'bold', fontSize: '9px', color: '#333', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>TOTAL PARTICIPANTS</span>
                <div className="info-value" style={{ borderBottom: '1px solid #ccc', padding: '8px 10px', minHeight: '28px', fontSize: '11px', background: '#f9fafb' }}>{costingData.pax}</div>
              </div>
              <div className="info-item">
                <span className="info-label" style={{ fontWeight: 'bold', fontSize: '9px', color: '#333', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>NO. OF DAYS</span>
                <div className="info-value" style={{ borderBottom: '1px solid #ccc', padding: '8px 10px', minHeight: '28px', fontSize: '11px', background: '#f9fafb' }}>{days}</div>
              </div>
              <div className="info-item" style={{ gridColumn: 'span 2' }}>
                <span className="info-label" style={{ fontWeight: 'bold', fontSize: '9px', color: '#333', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>PROGRAM</span>
                <div className="info-value" style={{ borderBottom: '1px solid #ccc', padding: '8px 10px', minHeight: '28px', fontSize: '11px', background: '#f9fafb' }}>{session.name}</div>
              </div>
            </div>
          </div>

          {/* Invoicing and Trainers Side by Side */}
          <div className="two-col">
            {/* Invoicing */}
            <div className="col-left">
              <div className="section">
                <div className="section-header">INVOICING</div>
                <table>
                  <thead>
                    <tr>
                      <th colSpan="2">INV NO: {costingData.invoice_number || 'N/A'}</th>
                      <th colSpan="2" className="text-right">DATE: {formatShortDate(costingData.invoice_date)}</th>
                    </tr>
                    <tr>
                      <th style={{ width: '10%' }}>QTY</th>
                      <th style={{ width: '50%' }}>PARTICULARS</th>
                      <th style={{ width: '20%' }}>RM/UNIT</th>
                      <th style={{ width: '20%' }}>TOTAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costingData.all_invoices && costingData.all_invoices.length > 1 ? (
                      <>
                        {costingData.all_invoices.map((inv, idx) => (
                          <tr key={idx}>
                            <td className="text-center">1</td>
                            <td>{inv.company_name || session.name} ({inv.invoice_number})</td>
                            <td className="text-right">{(inv.net_invoiced_value || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                            <td className="text-right">{(inv.net_invoiced_value || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </>
                    ) : (
                      <tr>
                        <td className="text-center">1</td>
                        <td>{session.name}</td>
                        <td className="text-right">{invoiceTotal.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                        <td className="text-right">{invoiceTotal.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    )}
                    <tr><td colSpan="4" style={{ height: '10px' }}></td></tr>
                    <tr className="subtotal-row">
                      <td colSpan="3" className="text-right">SUBTOTAL</td>
                      <td className="text-right">{invoiceTotal.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                      <td colSpan="3" className="text-right">Tax (if applicable)</td>
                      <td className="text-right">{taxAmount.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                    </tr>
                    <tr className="total-row">
                      <td colSpan="3" className="text-right">TOTAL</td>
                      <td className="text-right">{invoiceTotal.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Trainers */}
            <div className="col-right">
              <div className="section">
                <div className="section-header">TRAINERS</div>
                <table>
                  <thead>
                    <tr>
                      <th>NAME</th>
                      <th>ROLE</th>
                      <th>CLAIM (RM)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(costingData.trainer_fees || []).map((fee, idx) => (
                      <tr key={idx}>
                        <td>{fee.trainer_name}</td>
                        <td style={{ textTransform: 'capitalize' }}>{fee.role}</td>
                        <td className="text-right">{(fee.fee_amount || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                    {costingData.coordinator_fee && coordFeeTotal > 0 && (
                      <tr>
                        <td>{costingData.coordinator_fee.coordinator_name || 'Coordinator'}</td>
                        <td>Coordinator</td>
                        <td className="text-right">{coordFeeTotal.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    )}
                    <tr><td colSpan="3" style={{ height: '20px' }}></td></tr>
                    <tr className="total-row">
                      <td colSpan="2" className="text-right">TOTAL</td>
                      <td className="text-right">{(trainerFeesTotal + coordFeeTotal).toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Expenses */}
          <div className="section">
            <div className="section-header">EXPENSES</div>
            <table>
              <thead>
                <tr>
                  <th style={{ width: '30%' }}>DESCRIPTION</th>
                  <th style={{ width: '12%' }}>RATE</th>
                  <th style={{ width: '10%' }}>BASE</th>
                  <th style={{ width: '18%', paddingLeft: '8px' }}>TOTAL (RM)</th>
                  <th style={{ width: '30%', paddingLeft: '10px' }}>REMARKS</th>
                </tr>
              </thead>
              <tbody>
                {(costingData.expenses || []).map((expense, idx) => {
                  const amount = expense.actual_amount || expense.estimated_amount || 0;
                  // Prefer the stored percentage_rate / per_pax_rate on the
                  // expense record (saved at the time of entry). Only fall
                  // back to a computed value when it is missing AND the
                  // divisor is non-zero — otherwise show "—" instead of
                  // Infinity%.
                  const safeInvTotal = Number(invoiceTotal) > 0 ? Number(invoiceTotal) : 0;
                  const safeHeadcount = Number(costingData.total_headcount) > 0
                    ? Number(costingData.total_headcount) : 0;
                  let rateLabel = 'Fixed';
                  let baseLabel = '-';
                  if (expense.expense_type === 'percentage') {
                    const pct = Number(expense.percentage_rate);
                    const derivedPct = safeInvTotal > 0 ? (amount / safeInvTotal) * 100 : null;
                    const displayPct = Number.isFinite(pct) && pct > 0 ? pct : derivedPct;
                    rateLabel = displayPct != null ? `${displayPct.toFixed(0)}%` : '—';
                    baseLabel = safeInvTotal > 0 ? safeInvTotal.toLocaleString() : '—';
                  } else if (expense.expense_type === 'per_pax') {
                    const rate = Number(expense.per_pax_rate);
                    const derivedRate = safeHeadcount > 0 ? (amount / safeHeadcount) : null;
                    const displayRate = Number.isFinite(rate) && rate > 0 ? rate : derivedRate;
                    rateLabel = displayRate != null ? `RM${displayRate.toFixed(0)}/pax` : '—';
                    baseLabel = safeHeadcount > 0 ? safeHeadcount : '—';
                  }
                  return (
                    <tr key={idx}>
                      <td>{getExpenseCategoryName(expense.category)}</td>
                      <td className="text-center">{rateLabel}</td>
                      <td className="text-center">{baseLabel}</td>
                      <td className="text-right" style={{ paddingRight: '10px' }}>{amount.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                      <td style={{ paddingLeft: '10px' }}>{expense.remark || ''}</td>
                    </tr>
                  );
                })}
                {(costingData.expenses?.length || 0) === 0 && (
                  <tr><td colSpan="5" className="text-center" style={{ padding: '15px', color: '#666' }}>No expenses recorded</td></tr>
                )}
                <tr className="total-row">
                  <td colSpan="3" className="text-right">TOTAL EXPENSES</td>
                  <td className="text-right" style={{ paddingRight: '10px' }}>{cashExpenses.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Costing Summary */}
          <div className="section">
            <div className="section-header">COSTING SUMMARY</div>
            <table className="costing-table">
              <tbody>
                <tr>
                  <td className="costing-label">TOTAL SALES</td>
                  <td className="costing-value">{invoiceTotal.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                  <td className="costing-pct"></td>
                </tr>
                <tr>
                  <td className="costing-label">LESS GST 6%</td>
                  <td className="costing-value">{taxAmount.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                  <td className="costing-pct"></td>
                </tr>
                <tr style={{ background: '#dbeafe' }}>
                  <td className="costing-label">GROSS REVENUE</td>
                  <td className="costing-value" style={{ fontWeight: 'bold' }} data-testid="claim-form-gross-revenue">
                    {financialTotalsUnavailable ? '—' : grossRevenue.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="costing-pct"></td>
                </tr>
                <tr>
                  <td className="costing-label">Cash Expenses</td>
                  <td className="costing-value">{cashExpenses.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                  <td className="costing-pct"></td>
                </tr>
                <tr>
                  <td className="costing-label">Trainers Claim</td>
                  <td className="costing-value">{(trainerFeesTotal + coordFeeTotal).toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                  <td className="costing-pct"></td>
                </tr>
                <tr className="highlight">
                  <td className="costing-label">Marketing ({marketingName})</td>
                  <td className="costing-value">{marketingAmount.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                  <td className="costing-pct"></td>
                </tr>
                <tr style={{ background: '#fef3c7' }}>
                  <td className="costing-label">TOTAL EXPENSES</td>
                  <td className="costing-value" style={{ fontWeight: 'bold' }} data-testid="claim-form-total-expenses">
                    {financialTotalsUnavailable ? '—' : totalExpenses.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="costing-pct"></td>
                </tr>
                <tr className="profit-row">
                  <td className="costing-label" style={{ fontSize: '11px' }}>NET PROFIT</td>
                  <td className="costing-value" style={{ fontSize: '12px' }} data-testid="claim-form-net-profit">
                    {financialTotalsUnavailable ? '—' : profit.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="costing-pct highlight" style={{ fontSize: '11px', fontWeight: 'bold' }} data-testid="claim-form-net-profit-pct">
                    {financialTotalsUnavailable ? '—' : `${profitPct.toFixed(2)}%`}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Acknowledgment */}
          <div className="section">
            <div className="section-header">ACKNOWLEDGMENT</div>
            <table className="ack-table">
              <tbody>
                <tr>
                  <td className="ack-label">Training Coordinator:</td>
                  <td></td>
                  <td style={{ width: '15%' }}>Sign:</td>
                  <td style={{ width: '20%' }}>Date:</td>
                </tr>
                <tr>
                  <td className="ack-label">Manager In Charge:</td>
                  <td></td>
                  <td>Sign:</td>
                  <td>Date:</td>
                </tr>
                <tr>
                  <td className="ack-label">Director:</td>
                  <td></td>
                  <td>Sign:</td>
                  <td>Date:</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="footer">
            {companySettings?.document_styling?.footer_tagline || 'Generated by Training Management System'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimFormPrint;
